import { z } from "zod";

// API error catalogue. Codes are pinned because the frontend maps them to
// localized copy via i18n keys (ADR-0030 §"Error response shape"); `message`
// is a server-rendered fallback for unknown codes, never the source of truth.
export const API_ERROR_CODES = [
  "unsupported_format",
  "animation_not_supported",
  "file_too_large",
  "file_too_small",
  "image_too_small",
  "image_too_large",
  "decode_bomb",
  "nsfw_rejected",
  "silhouette_thin_parts",
  "silhouette_holes",
  "silhouette_rough_edge",
  "not_found",
  "forbidden",
  "unauthorized_internal",
  "job_already_claimed",
  "job_not_running",
  "rate_limited",
  "payments_disabled",
  "quantity_exceeds_max",
  "paczkomat_unavailable_oversize",
  "invalid_product_type",
  "internal_error",
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];
export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES);

// Discriminated union: `code` narrows the carried extras (e.g. `file_too_large`
// always brings `max_size_mb`). The frontend `error.code === 'image_too_small'`
// branch sees `min_dimension_px` typed without an extra cast.
export const ApiError = z.discriminatedUnion("code", [
  z.object({
    code: z.literal("file_too_large"),
    message: z.string().optional(),
    max_size_mb: z.number().positive(),
  }),
  z.object({
    code: z.literal("image_too_small"),
    message: z.string().optional(),
    min_dimension_px: z.number().positive().int(),
  }),
  z.object({
    code: z.literal("image_too_large"),
    message: z.string().optional(),
    max_dimension_px: z.number().positive().int(),
  }),
  z.object({ code: z.literal("unsupported_format"), message: z.string().optional() }),
  z.object({ code: z.literal("animation_not_supported"), message: z.string().optional() }),
  z.object({ code: z.literal("file_too_small"), message: z.string().optional() }),
  z.object({ code: z.literal("decode_bomb"), message: z.string().optional() }),
  z.object({ code: z.literal("nsfw_rejected"), message: z.string().optional() }),
  z.object({
    code: z.literal("silhouette_thin_parts"),
    message: z.string().optional(),
    // The detected pet species (e.g. "cat"/"dog"), so the frontend can name the
    // pet in the rejection copy. Optional — absent on the fallback path or when
    // detection was inconclusive; the frontend uses a generic noun then.
    subject_species: z.string().optional(),
  }),
  // A foreground occluder (grass, a leash, a hand) punched a see-through hole
  // through the silhouette; carries `subject_species` for the same warm copy.
  z.object({
    code: z.literal("silhouette_holes"),
    message: z.string().optional(),
    subject_species: z.string().optional(),
  }),
  // Ground clutter along the bottom broke the outline into a ragged fringe;
  // carries `subject_species` for the same warm copy.
  z.object({
    code: z.literal("silhouette_rough_edge"),
    message: z.string().optional(),
    subject_species: z.string().optional(),
  }),
  z.object({ code: z.literal("not_found"), message: z.string().optional() }),
  z.object({ code: z.literal("forbidden"), message: z.string().optional() }),
  z.object({ code: z.literal("unauthorized_internal"), message: z.string().optional() }),
  z.object({ code: z.literal("job_already_claimed"), message: z.string().optional() }),
  z.object({ code: z.literal("job_not_running"), message: z.string().optional() }),
  z.object({ code: z.literal("rate_limited"), message: z.string().optional() }),
  // `payments_disabled` kill-switch (ADR-0031): the checkout endpoint returns
  // this when the `payments_disabled` feature flag is on. The frontend maps it
  // to the "Payments temporarily unavailable" copy.
  z.object({ code: z.literal("payments_disabled"), message: z.string().optional() }),
  // `checkout_max_quantity` setting (#240): the checkout endpoint returns this
  // when the posted quantity exceeds the current operator-editable ceiling. Carries
  // `max_quantity` so the frontend can name the live bound; normally unreachable
  // via the UI (the stepper clamps to `quote.maxQuantity`), so it guards a stale
  // quote / direct API call.
  z.object({
    code: z.literal("quantity_exceeds_max"),
    message: z.string().optional(),
    max_quantity: z.number().positive().int(),
  }),
  // Cart oversize rule (ADR-0054 §4, #406): the checkout endpoint returns this when the
  // buyer's cart exceeds the Paczkomat capacity threshold yet the posted `delivery` is a
  // Paczkomat — a locker delivery an oversize order cannot use, and which has no courier
  // address to fall back to. The frontend re-quotes, sees `shipping.oversize`, and forces
  // Kurier. Normally unreachable via the UI (it withdraws Paczkomat on an oversize quote),
  // so this guards a stale quote / direct API call — server-authoritative.
  z.object({ code: z.literal("paczkomat_unavailable_oversize"), message: z.string().optional() }),
  // The generic "add more" create route (`POST /products`, ADR-0054 §2) is pillow-only by
  // design — a non-pillow `productType` (e.g. `3d_print`, which has its own bespoke
  // `POST /products/3d-print` create) is rejected with a clean 400 rather than resolving a
  // nonsense pillow variant. Guards a direct API call; the UI never posts another type here.
  z.object({ code: z.literal("invalid_product_type"), message: z.string().optional() }),
  z.object({ code: z.literal("internal_error"), message: z.string().optional() }),
]);
export type ApiError = z.infer<typeof ApiError>;

export const ApiErrorEnvelope = z.object({ error: ApiError });
export type ApiErrorEnvelope = z.infer<typeof ApiErrorEnvelope>;
